<template>
    <app-layout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Manage Variables
            </h2>
        </template>

        <div>
            <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
                <FormSection @submitted="createNewVariable">
                    <template #title>
                        Create a new variable
                    </template>

                    <template #description>
                        Variables can be created here on the basis of the files supplied or the desired definition.
                        Variables from files must have the same value as the column in the files as "value". Otherwise
                        the variables will not be displayed correctly in the graph.<br>
                        <br>
                        Each variable can have a category for the dropdown menu and a category for the definition page.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="variable_category_id" value="Category (Graphs)"/>
                            <Select id="variable_category_id" class="mt-1 block w-full"
                                    v-model="createVariableForm.variable_category_id"
                                    ref="createVariableCategoryId"
                                    :options="categories"
                                    option-value="label"
                                    option-key="id"
                                    :default-text="null"
                            />
                            <InputError :message="createVariableForm.errors.variable_category_id" class="mt-2"/>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="variable_definition_category_id" value="Category (Definition Page)"/>
                            <Select id="variable_definition_category_id" class="mt-1 block w-full"
                                    v-model="createVariableForm.variable_definition_category_id"
                                    ref="createVariableDefinitionCategoryId"
                                    :options="categories"
                                    option-value="label"
                                    option-key="id"
                                    :default-text="null"
                            />
                            <InputError :message="createVariableForm.errors.variable_definition_category_id"
                                        class="mt-2"/>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="value" value="Value"/>
                            <Input id="value" type="text" class="mt-1 block w-full"
                                   v-model="createVariableForm.value"
                                   autocomplete="variable-value" ref="createVariableValue"/>
                            <InputError :message="createVariableForm.errors.value" class="mt-2"/>
                            <HelpText>
                                Must match the column in the files for graphs.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="label" value="Label"/>
                            <Input id="label" type="text" class="mt-1 block w-full"
                                   v-model="createVariableForm.label"
                                   autocomplete="variable-label" ref="createVariableLabel"/>
                            <InputError :message="createVariableForm.errors.label" class="mt-2"/>
                            <HelpText>
                                Only required for variables that should be displayed in the graphs variable dropdown
                                menu.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="definition" value="Definition/Description"/>
                            <Textarea rows="5" id="definition" class="mt-1 block w-full"
                                      v-model="createVariableForm.definition"
                                      ref="createVariableDefinition"/>
                            <InputError :message="createVariableForm.errors.definition" class="mt-2"/>
                            <HelpText>
                                The definition is displayed below the graphs and on the definition page.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="dhs_implementation" value="DHS implementation"/>
                            <Textarea rows="5" id="dhs_implementation" class="mt-1 block w-full"
                                      v-model="createVariableForm.dhs_implementation"
                                      ref="createVariableDhsImplementation"/>
                            <InputError :message="createVariableForm.errors.dhs_implementation" class="mt-2"/>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <Label for="ipums_implementation" value="IPUMS implementation"/>
                            <Textarea rows="5" id="ipums_implementation" class="mt-1 block w-full"
                                      v-model="createVariableForm.ipums_implementation"
                                      ref="createVariableIpumsImplementation"/>
                            <InputError :message="createVariableForm.errors.ipums_implementation" class="mt-2"/>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_graphable" v-model="createVariableForm.is_graphable"/>
                                    <span class="ml-2 text-sm text-gray-600">Display in the variable selection dropdown on the graph page.</span>
                                </label>
                                <InputError :message="createVariableForm.errors.is_graphable" class="mt-2"/>
                            </div>

                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_enabled" v-model="createVariableForm.is_enabled"/>
                                    <span class="ml-2 text-sm text-gray-600">Variable is enabled.</span>
                                </label>
                                <InputError :message="createVariableForm.errors.is_enabled" class="mt-2"/>
                            </div>

                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_special" v-model="createVariableForm.is_special"/>
                                    <span class="ml-2 text-sm text-gray-600">If checked, y-axis calculation is done by google charts itself.</span>
                                </label>
                                <InputError :message="createVariableForm.errors.is_special" class="mt-2"/>
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createVariableForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createVariableForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createVariableForm.processing }"
                                :disabled="createVariableForm.processing">
                            Create
                        </Button>
                    </template>
                </FormSection>

                <template v-if="variables.length > 0">
                    <SectionBorder/>

                    <div class="flex items-center mb-6 mt-10 sm:mt-0 px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">Variables ({{ variables.length }})</h3>

                        <div class="ml-auto">
                            <Input
                                id="search"
                                type="text"
                                placeholder="Filter..."
                                class="block w-full"
                                v-model="variableSearch"
                            />
                        </div>
                    </div>

                    <table
                        class="w-full sm:rounded-md bg-white divide-y divide-gray-300 overflow-hidden shadow">
                        <thead class="bg-gray-900">
                        <tr class="text-white">
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left sm:w-48">value</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left hidden sm:table-cell">
                                information
                            </th>
                            <th class="font-semibold whitespace-nowrap text-sm uppercase px-6 py-4 text-right w-1">&nbsp;</th>
                        </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">
                        <tr v-for="(variable, idx) in variableSearchResults" :key="idx"
                            :class="{'bg-gray-100' : idx % 2 !== 0}">
                            <td class="px-6 py-4 sm:w-48">
                                {{ variable.value }}
                            </td>

                            <td class="px-6 py-4 hidden sm:table-cell">
                                <span
                                    class="bg-red-100 text-red-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                    v-if="!variable.is_enabled"
                                >
                                    Globally Disabled
                                </span>

                                <template v-if="variable.is_enabled">
                                    <span
                                        class="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="variable.variable_category_id"
                                    >
                                        Graph: {{ variable.variable_category.label }}
                                    </span>

                                    <span
                                        class="bg-purple-100 text-purple-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="variable.variable_definition_category_id"
                                    >
                                        Definition: {{ variable.variable_definition_category.label }}
                                    </span>

                                    <span
                                        class="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="variable.is_graphable"
                                    >
                                        Enabled for Graphs
                                    </span>

                                    <span
                                        class="bg-yellow-100 text-yellow-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="variable.is_special"
                                    >
                                        Automated Y-Axis
                                    </span>

                                    <div v-if="variable.variable_category_id" class="mt-2 text-sm">
                                        <span class="text-gray-400">Label</span><br>
                                        {{ variable.label }}
                                    </div>

                                    <div class="mt-2 text-sm">
                                        <span class="text-gray-400">Definition/Description</span><br>
                                        {{ variable.definition }}
                                    </div>
                                </template>
                            </td>

                            <td class="whitespace-nowrap px-6 py-4 text-left w-1">
                                <DangerButton @click.native="confirmVariableDeletion(variable)" title="Delete"
                                              class="mr-2">
                                    <font-awesome-icon
                                        :icon="['fad', 'trash']"
                                    ></font-awesome-icon>
                                </DangerButton>

                                <SecondaryButton @click.native="editVariable(variable)" title="Edit">
                                    <font-awesome-icon
                                        :icon="['fad', 'pencil']"
                                    ></font-awesome-icon>
                                </SecondaryButton>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <DialogModal :show="confirmingVariableDeletion" @close="closeConfirmVariableDeletion">
                        <template #title>
                            Delete Variable
                        </template>

                        <template #content>
                            Are you sure you want to delete this variable?
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeConfirmVariableDeletion">
                                Nevermind
                            </SecondaryButton>

                            <DangerButton
                                class="ml-2"
                                @click.native="deleteVariable"
                                :class="{ 'opacity-25': processing }"
                                :disabled="processing"
                            >
                                Delete Variable
                            </DangerButton>
                        </template>
                    </DialogModal>

                    <FormModal :show="editingVariable" @submitted="submitEditVariable">
                        <template #title>
                            Edit Variable
                        </template>

                        <template #content>
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <Label for="variable_category_id" value="Category (Graphs)"/>
                                    <Select id="variable_category_id" class="mt-1 block w-full"
                                            v-model="editVariableForm.variable_category_id"
                                            ref="editVariableCategoryId"
                                            :options="categories"
                                            option-value="label"
                                            option-key="id"
                                            :default-text="null"
                                    />
                                    <InputError :message="editVariableForm.errors.variable_category_id" class="mt-2"/>
                                </div>

                                <div class="col-span-6">
                                    <Label for="variable_definition_category_id" value="Category (Definition Page)"/>
                                    <Select id="variable_definition_category_id" class="mt-1 block w-full"
                                            v-model="editVariableForm.variable_definition_category_id"
                                            ref="editVariableDefinitionCategoryId"
                                            :options="categories"
                                            option-value="label"
                                            option-key="id"
                                            :default-text="null"
                                    />
                                    <InputError :message="editVariableForm.errors.variable_definition_category_id"
                                                class="mt-2"/>
                                </div>

                                <div class="col-span-6">
                                    <Label for="value" value="Value"/>
                                    <Input id="value" type="text" class="mt-1 block w-full"
                                           v-model="editVariableForm.value"
                                           autocomplete="variable-value" ref="editVariableValue"/>
                                    <InputError :message="editVariableForm.errors.value" class="mt-2"/>
                                    <HelpText>
                                        Must match the column in the files for graphs.
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <Label for="label" value="Label"/>
                                    <Input id="label" type="text" class="mt-1 block w-full"
                                           v-model="editVariableForm.label"
                                           autocomplete="variable-label" ref="editVariableLabel"/>
                                    <InputError :message="editVariableForm.errors.label" class="mt-2"/>
                                    <HelpText>
                                        Only required for variables that should be displayed in the graphs variable dropdown
                                        menu.
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <Label for="definition" value="Definition/Description"/>
                                    <Textarea rows="5" id="definition" class="mt-1 block w-full"
                                              v-model="editVariableForm.definition"
                                              ref="editVariableDefinition"/>
                                    <InputError :message="editVariableForm.errors.definition" class="mt-2"/>
                                    <HelpText>
                                        The definition is displayed below the graphs and on the definition page.
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <Label for="dhs_implementation" value="DHS implementation"/>
                                    <Textarea rows="5" id="dhs_implementation" class="mt-1 block w-full"
                                              v-model="editVariableForm.dhs_implementation"
                                              ref="editVariableDhsImplementation"/>
                                    <InputError :message="editVariableForm.errors.dhs_implementation" class="mt-2"/>
                                </div>

                                <div class="col-span-6">
                                    <Label for="ipums_implementation" value="IPUMS implementation"/>
                                    <Textarea rows="5" id="ipums_implementation" class="mt-1 block w-full"
                                              v-model="editVariableForm.ipums_implementation"
                                              ref="editVariableIpumsImplementation"/>
                                    <InputError :message="editVariableForm.errors.ipums_implementation" class="mt-2"/>
                                </div>

                                <div class="col-span-6">
                                    <div class="block mt-1 w-full">
                                        <label class="flex items-center">
                                            <Checkbox name="is_graphable" v-model="editVariableForm.is_graphable"/>
                                            <span class="ml-2 text-sm text-gray-600">Display in the variable selection dropdown on the graph page.</span>
                                        </label>
                                        <InputError :message="editVariableForm.errors.is_graphable" class="mt-2"/>
                                    </div>

                                    <div class="block mt-1 w-full">
                                        <label class="flex items-center">
                                            <Checkbox name="is_enabled" v-model="editVariableForm.is_enabled"/>
                                            <span class="ml-2 text-sm text-gray-600">Variable is enabled.</span>
                                        </label>
                                        <InputError :message="editVariableForm.errors.is_enabled" class="mt-2"/>
                                    </div>

                                    <div class="block mt-1 w-full">
                                        <label class="flex items-center">
                                            <Checkbox name="is_special" v-model="editVariableForm.is_special"/>
                                            <span class="ml-2 text-sm text-gray-600">If checked, y-axis calculation is done by google charts itself.</span>
                                        </label>
                                        <InputError :message="editVariableForm.errors.is_special" class="mt-2"/>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <Label for="order" value="Order"/>
                                    <Input id="order" type="text" class="mt-1 block w-full"
                                           v-model="editVariableForm.order"
                                           autocomplete="variable-order" ref="editVariableOrder"/>
                                    <InputError :message="editVariableForm.errors.order" class="mt-2"/>
                                    <HelpText>
                                        On which position the variable should be shown.
                                    </HelpText>
                                </div>
                            </div>
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeEditVariableModal">
                                Nevermind
                            </SecondaryButton>

                            <Button type="submit"
                                    :class="{ 'opacity-25': editVariableForm.processing }"
                                    :disabled="editVariableForm.processing"
                            >
                                Save
                            </Button>
                        </template>
                    </FormModal>
                </template>

                <SectionBorder/>

                <FormSection class="mt-10 sm:mt-0" @submitted="createNewVariableCategory">
                    <template #title>
                        Create a new variable category
                    </template>

                    <template #description>
                        Variable categories are used to group variables together. They are used to display variables on
                        the definition page and to group variables in the variable selection dropdown on the graph page.
                    </template>

                    <template #form>
                        <div class="col-span-6 sm:col-span-4">
                            <Label for="label" value="Label"/>
                            <Input id="label" type="text" class="mt-1 block w-full"
                                   v-model="createVariableCategoryForm.label"
                                   autocomplete="category-label" ref="createCategoryLabel"/>
                            <InputError :message="createVariableCategoryForm.errors.label" class="mt-2"/>
                            <HelpText>
                                The label of the variable category.
                            </HelpText>
                        </div>

                        <div class="col-span-6 sm:col-span-4">
                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_definition" v-model="createVariableCategoryForm.is_definition"/>
                                    <span class="ml-2 text-sm text-gray-600">Display on the definition page.</span>
                                </label>
                                <InputError :message="createVariableCategoryForm.errors.is_definition" class="mt-2"/>
                            </div>

                            <div class="block mt-1 w-full">
                                <label class="flex items-center">
                                    <Checkbox name="is_graphable" v-model="createVariableCategoryForm.is_graphable"/>
                                    <span class="ml-2 text-sm text-gray-600">Display in the variable selection dropdown on the graph page.</span>
                                </label>
                                <InputError :message="createVariableCategoryForm.errors.is_graphable" class="mt-2"/>
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <ActionMessage :on="createVariableCategoryForm.recentlySuccessful" class="mr-3">
                            Created.
                        </ActionMessage>

                        <ActionMessage :on="createVariableCategoryForm.processing" class="mr-3">
                            Creating...
                        </ActionMessage>

                        <Button :class="{ 'opacity-25': createVariableCategoryForm.processing }"
                                :disabled="createVariableCategoryForm.processing">
                            Create
                        </Button>
                    </template>
                </FormSection>

                <template v-if="categories.length > 0">
                    <SectionBorder/>

                    <div class="flex items-center mb-6 mt-10 sm:mt-0 px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">Variable Categories</h3>

                        <div class="ml-auto">
                            <Input
                                id="search"
                                type="text"
                                placeholder="Filter..."
                                class="block w-full"
                                v-model="categorySearch"
                            />
                        </div>
                    </div>

                    <table
                        class="w-full whitespace-nowrap sm:rounded-md bg-white divide-y divide-gray-300 overflow-hidden shadow">
                        <thead class="bg-gray-900">
                        <tr class="text-white">
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-left">label</th>
                            <th class="font-semibold text-sm uppercase px-6 py-4 text-right w-1">&nbsp;</th>
                        </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">
                        <tr v-for="(category, idx) in categorySearchResults" :key="idx"
                            :class="{'bg-gray-100' : idx % 2 !== 0}">
                            <td class="px-6 py-4 flex align-center">
                                {{ category.label }}
                                <div class="ml-auto">
                                    <span
                                        class="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="category.is_graphable">Graph</span>
                                    <span
                                        class="bg-purple-100 text-purple-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded"
                                        v-if="category.is_definition">Definition</span>
                                </div>
                            </td>

                            <td class="px-6 py-4 text-left w-1">
                                <DangerButton @click.native="confirmCategoryDeletion(category)" title="Delete"
                                              class="mr-2">
                                    <font-awesome-icon
                                        :icon="['fad', 'trash']"
                                    ></font-awesome-icon>
                                </DangerButton>

                                <SecondaryButton @click.native="editCategory(category)" title="Edit">
                                    <font-awesome-icon
                                        :icon="['fad', 'pencil']"
                                    ></font-awesome-icon>
                                </SecondaryButton>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <DialogModal :show="confirmingCategoryDeletion" @close="closeConfirmCategoryDeletion">
                        <template #title>
                            Delete Category
                        </template>

                        <template #content>
                            Are you sure you want to delete this category?
                            <br><span
                            class="text-red-500">Can only be deleted if no variable is assigned anymore.</span>

                            <InputError :message="deleteError" class="mt-6"/>
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeConfirmCategoryDeletion">
                                Nevermind
                            </SecondaryButton>

                            <DangerButton
                                class="ml-2"
                                @click.native="deleteCategory"
                                :class="{ 'opacity-25': processing }"
                                :disabled="processing"
                            >
                                Delete Category
                            </DangerButton>
                        </template>
                    </DialogModal>

                    <FormModal :show="editingCategory" @submitted="submitEditCategory">
                        <template #title>
                            Edit Variable Category
                        </template>

                        <template #content>
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <Label for="label" value="Label"/>
                                    <Input id="label" type="text" class="mt-1 block w-full"
                                           v-model="editVariableCategoryForm.label"
                                           autocomplete="category-label" ref="editCategoryLabel"/>
                                    <InputError :message="editVariableCategoryForm.errors.label" class="mt-2"/>
                                    <HelpText>
                                        The label of the variable category.
                                    </HelpText>
                                </div>

                                <div class="col-span-6">
                                    <div class="block mt-1 w-full">
                                        <label class="flex items-center">
                                            <Checkbox name="remember" v-model="editVariableCategoryForm.is_definition"/>
                                            <span
                                                class="ml-2 text-sm text-gray-600">Display on the definition page.</span>
                                        </label>
                                        <InputError :message="editVariableCategoryForm.errors.is_definition"
                                                    class="mt-2"/>
                                    </div>

                                    <div class="block mt-1 w-full">
                                        <label class="flex items-center">
                                            <Checkbox name="remember" v-model="editVariableCategoryForm.is_graphable"/>
                                            <span class="ml-2 text-sm text-gray-600">Display in the variable selection dropdown on the graph page.</span>
                                        </label>
                                        <InputError :message="editVariableCategoryForm.errors.is_graphable"
                                                    class="mt-2"/>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <Label for="order" value="Order"/>
                                    <Input id="order" type="text" class="mt-1 block w-full"
                                           v-model="editVariableCategoryForm.order"
                                           autocomplete="category-order" ref="editCategoryOrder"/>
                                    <InputError :message="editVariableCategoryForm.errors.order" class="mt-2"/>
                                    <HelpText>
                                        On which position the variable category should be shown.
                                    </HelpText>
                                </div>
                            </div>
                        </template>

                        <template #footer>
                            <SecondaryButton @click.native="closeEditCategoryModal">
                                Nevermind
                            </SecondaryButton>

                            <Button type="submit"
                                    :class="{ 'opacity-25': editVariableCategoryForm.processing }"
                                    :disabled="editVariableCategoryForm.processing"
                            >
                                Save
                            </Button>
                        </template>
                    </FormModal>
                </template>
            </div>
        </div>
    </app-layout>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout';
import FormSection from "@/Jetstream/FormSection";
import Checkbox from "@/Jetstream/Checkbox";
import Label from "@/Jetstream/Label";
import Input from "@/Jetstream/Input";
import InputError from "@/Jetstream/InputError";
import HelpText from "@/Jetstream/HelpText";
import ActionMessage from "@/Jetstream/ActionMessage";
import Button from "@/Jetstream/Button";
import SectionBorder from "@/Jetstream/SectionBorder";
import DangerButton from "@/Jetstream/DangerButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DialogModal from "@/Jetstream/DialogModal";
import FormModal from "@/Jetstream/FormModal";
import Textarea from "@/Jetstream/Textarea";
import Select from "@/Jetstream/Select";
import UpdatePasswordForm from "@/Pages/Profile/UpdatePasswordForm.vue";

export default {
    components: {
        UpdatePasswordForm,
        Select,
        Textarea,
        FormModal,
        DialogModal,
        SecondaryButton,
        DangerButton,
        SectionBorder,
        Button,
        ActionMessage,
        HelpText,
        InputError,
        Input,
        Label,
        Checkbox,
        FormSection,
        AppLayout,
    },

    props: ['categories', 'variables'],

    data() {
        return {
            categorySearch: null,
            variableSearch: null,

            confirmingCategoryDeletion: false,
            deleteError: null,
            selectedCategory: null,

            editingCategory: false,

            confirmingVariableDeletion: false,
            selectedVariable: null,

            editingVariable: false,

            processing: false,

            createVariableCategoryForm: this.$inertia.form({
                label: '',
                is_definition: false,
                is_graphable: true
            }),

            editVariableCategoryForm: this.$inertia.form({
                label: '',
                is_definition: false,
                is_graphable: true,
                order: '',
            }),

            createVariableForm: this.$inertia.form({
                variable_category_id: '',
                variable_definition_category_id: '',
                value: '',
                label: '',
                definition: '',
                dhs_implementation: '',
                ipums_implementation: '',
                is_graphable: true,
                is_enabled: true,
                is_special: false,
            }),

            editVariableForm: this.$inertia.form({
                variable_category_id: '',
                variable_definition_category_id: '',
                value: '',
                label: '',
                definition: '',
                dhs_implementation: '',
                ipums_implementation: '',
                is_graphable: true,
                is_enabled: true,
                is_special: false,
                order: '',
            }),
        }
    },

    computed: {
        categorySearchResults() {
            if (this.categorySearch === null) {
                return this.categories;
            }

            return this.categories.filter(e => e.label.includes(this.categorySearch));
        },

        variableSearchResults() {
            if (this.variableSearch === null) {
                return this.variables;
            }

            return this.variables.filter(e => (e.label && e.label.includes(this.variableSearch)) || (e.value && e.value.includes(this.variableSearch)));
        },
    },

    methods: {
        closeEditVariableModal() {
            this.editingVariable = false;
            this.selectedVariable = null;
            this.editVariableForm.reset();
        },

        editVariable(variable) {
            this.selectedVariable = variable;

            this.editVariableForm.variable_category_id = variable.variable_category_id
            this.editVariableForm.variable_definition_category_id = variable.variable_definition_category_id
            this.editVariableForm.value = variable.value
            this.editVariableForm.label = variable.label
            this.editVariableForm.definition = variable.definition
            this.editVariableForm.dhs_implementation = variable.dhs_implementation
            this.editVariableForm.ipums_implementation = variable.ipums_implementation
            this.editVariableForm.is_graphable = variable.is_graphable
            this.editVariableForm.is_enabled = variable.is_enabled
            this.editVariableForm.is_special = variable.is_special
            this.editVariableForm.order = variable.order

            this.editingVariable = true;
        },

        submitEditVariable() {
            this.editVariableForm.put(route('intern.variables.update', this.selectedVariable.id), {
                errorBag: 'updateVariable',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeEditVariableModal()
                },
            })
        },

        deleteVariable() {
            this.processing = true;

            this.$inertia.delete(route('intern.variables.destroy', this.selectedVariable.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeConfirmVariableDeletion();
                },
                onFinish: () => {
                    this.processing = false;
                }
            });
        },

        closeConfirmVariableDeletion() {
            this.confirmingVariableDeletion = false;
            this.selectedVariable = null;
        },

        confirmVariableDeletion(variable) {
            this.selectedVariable = variable;
            this.confirmingVariableDeletion = true;
        },

        createNewVariable() {
            this.createVariableForm.post(route('intern.variables.create'), {
                errorBag: 'createVariable',
                preserveScroll: true,
                onSuccess: () => {
                    this.createVariableForm.reset();
                },
            });
        },

        closeEditCategoryModal() {
            this.editingCategory = false;
            this.selectedCategory = null;
            this.editVariableCategoryForm.reset();
        },

        editCategory(category) {
            this.selectedCategory = category;

            this.editVariableCategoryForm.label = category.label;
            this.editVariableCategoryForm.is_definition = category.is_definition;
            this.editVariableCategoryForm.is_graphable = category.is_graphable;
            this.editVariableCategoryForm.order = category.order;

            this.editingCategory = true;
        },

        submitEditCategory() {
            this.editVariableCategoryForm.put(route('intern.variables.categories.update', this.selectedCategory.id), {
                errorBag: 'updateCategory',
                preserveScroll: true,
                onSuccess: () => {
                    this.closeEditCategoryModal()
                },
            })
        },

        deleteCategory() {
            this.processing = true;

            this.$inertia.delete(route('intern.variables.categories.destroy', this.selectedCategory.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeConfirmCategoryDeletion();
                },
                onError: (e) => {
                    this.deleteError = e[0];
                },
                onFinish: () => {
                    this.processing = false;
                }
            });
        },

        closeConfirmCategoryDeletion() {
            this.confirmingCategoryDeletion = false;
            this.deleteError = null;
            this.selectedCategory = null;
        },

        confirmCategoryDeletion(category) {
            this.selectedCategory = category;
            this.confirmingCategoryDeletion = true;
        },

        createNewVariableCategory() {
            this.createVariableCategoryForm.post(route('intern.variables.categories.create'), {
                errorBag: 'createVariableCategory',
                preserveScroll: true,
                onSuccess: () => {
                    this.createVariableCategoryForm.reset();
                },
            });
        }
    }
}
</script>
